import React, { useState, useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { hotjar } from 'react-hotjar';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import { PrivateRoute, PublicRoute } from './ProtectedRoute';

import config from './config';
import { URL } from './constants';

// demo pages
import Analytics from './pages/Analytics/analytics';
import AttendeeEvent from './pages/AttendeePortal/attendeeEvent';
import AttendeePortal from './pages/AttendeePortal/attendeePortal';
import Brand from './pages/Brand/brand';

import EventCheckIn from './pages/CheckIn';
import CheckInRedirect from './pages/CheckIn/checkInRedirect';

import Home from './pages/Dashboard/Home/home';
import Projects from './pages/Dashboard/Projects/projects';

import Demo from './pages/Demo';

import Error from './pages/General/error';
import PageSpinner from './pages/General/pageSpinner';
import PaymentRedirect from './pages/General/paymentRedirect';

import HelpMain from './pages/Help';
import HelpArticle from './pages/Help/helpArticle';

import ManagedServices from './pages/ManagedServices/managedServices';

import CreateAccount from './pages/Onboarding/CreateAccount/createAccount';
import EmailConfirmation from './pages/Onboarding/EmailConfirmation/emailConfirmation';
import EnterEmail from './pages/Onboarding/EnterEmail/enterEmail';
import Login from './pages/Onboarding/Login/login';
import ResetPassword from './pages/Onboarding/ResetPassword/resetPassword';

import ProjectAnalytics from './pages/ProjectDashboard/Analytics';
import ProjectMissingAssets from './pages/ProjectDashboard/MissingAssets';
import ProjectSettings from './pages/ProjectDashboard/Settings';

import ReviewVideo from './pages/ReviewVideo/reviewVideo';
import ReviewVideoGenerating from './pages/ReviewVideo/reviewVideoGenerating';

import ShareHub from './pages/Share/shareHub';
import ShareVideo from './pages/Share/shareVideo';

import LinkedInCallback from './pages/ThirdPartyAuth/LinkedIn';

import Analysis from './pages/VideoBuilder/Analysis';
import Review from './pages/VideoBuilder/Analysis/review';
import AssigneeVB from './pages/VideoBuilder/AssigneeVB';
import Clips from './pages/VideoBuilder/Clips';
import HighlightReel from './pages/VideoBuilder/HighlightReel';
import People from './pages/VideoBuilder/People';
import Share from './pages/VideoBuilder/Share';
import VideoBuilder from './pages/VideoBuilder/VB';

import './App.css';
import './styles/index.css';
import './styles/animation.css';
import 'react-dropzone-uploader/dist/styles.css';
import 'react-toastify/dist/ReactToastify.css';

import { getUserData } from './services/api/auth';
import { getUserEvents } from './services/api/events';
import { getSessionFromToken } from './services/api/sessions';
import {
  initializeTracker,
  setUserID,
} from './services/googleAnalyticsService';
import { initLR, identifyUserLR } from './services/logrocketService';

import { useProjectStore } from './stores/project';
import { useUserStore } from './stores/user';

import {
  goTo,
  validateId,
  getUrl,
  isProduction,
  getNameForTracking,
} from './utils';

const App = () => {
  const { user, accessToken, setUser, setAccessToken, clearAccessToken } =
    useUserStore();
  const { setUserEvents } = useProjectStore();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.onerror = console.error;

    const originalResizeObserver = ResizeObserver;

    window.ResizeObserver = class extends originalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        let timeoutId: NodeJS.Timeout;
        super((entries, observer) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => callback(entries, observer), 100);
        });
      }
    };
  }, []);

  const fetchUserData = async () => {
    const session = await getSessionFromToken(accessToken!);

    if (!session) throw new (Error as any)('Invalid session');

    const userData = await getUserData(session.userId);

    const userEvents = await getUserEvents(session.userId);
    setUserEvents(userEvents);

    const user = { ...userData, session };
    setUser(user);
  };

  useEffect(() => {
    if (user == null && accessToken) {
      fetchUserData()
        .then(() => {
          setAccessToken(accessToken);
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setUser(null);
          clearAccessToken();
          goTo(URL.ENTER_EMAIL);
        });
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken]);

  // initialize trackers
  useEffect(() => {
    // google analytics
    if (config.googleAnalyticsTagID)
      initializeTracker(config.googleAnalyticsTagID);

    // logrocket
    initLR();

    // facebook pixel
    if (isProduction()) ReactPixel.pageView();
  }, []);

  // set IDs when user is logged in
  useEffect(() => {
    if (!user) return;

    // google analytics
    if (config.googleAnalyticsTagID) setUserID(user.userId);

    // logrocket
    identifyUserLR(user);

    // user guiding
    if (window.userGuiding)
      window.userGuiding.identify(user.userId, {
        name: getNameForTracking(user),
        email: user.email,
      });
  }, [user]);

  useEffect(() => {
    if (isProduction()) {
      window?.fbq('track', 'PageView');

      if (config.hotjarID)
        hotjar.initialize({ id: Number(config.hotjarID), sv: 6 });
    }
  }, []);

  if (isLoading) return <PageSpinner isFullPage noTips />;

  const ProjectRedirect = () => {
    const { eventId = '' } = useParams();

    if (validateId(eventId ?? ''))
      return <Navigate to={getUrl(URL.VIDEO_BUILDER, eventId)} replace />;

    return <Error code={404} />;
  };

  return (
    <div className="app w-screen h-screen">
      <Routes>
        {/* onboarding */}
        <Route
          path={URL.ENTER_EMAIL}
          element={
            <PublicRoute>
              <EnterEmail />
            </PublicRoute>
          }
        />
        <Route
          path={URL.CREATE_ACCOUNT}
          element={
            <PublicRoute>
              <CreateAccount />
            </PublicRoute>
          }
        />
        <Route
          path={URL.LOGIN}
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path={URL.RESET_PASSWORD}
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path={URL.EMAIL_VERIFICATION}
          element={
            <PublicRoute>
              <EmailConfirmation type="verification" />
            </PublicRoute>
          }
        />
        <Route
          path={URL.FORGOT_PASSWORD}
          element={
            <PublicRoute>
              <EmailConfirmation type="reset-password" />
            </PublicRoute>
          }
        />

        {/* user dashboard */}
        <Route
          path={URL.HOME}
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path={URL.DASH_PROJECTS}
          element={
            <PrivateRoute>
              <Projects />
            </PrivateRoute>
          }
        />
        <Route
          path={URL.DASH_BRAND}
          element={
            <PrivateRoute>
              <Brand />
            </PrivateRoute>
          }
        />
        <Route
          path={URL.DASH_ANALYTICS}
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />

        {/* video builder */}
        <Route path="/:eventId" element={<ProjectRedirect />} />
        <Route
          path={`/:eventId${URL.VIDEO_BUILDER}`}
          element={
            <PrivateRoute>
              <VideoBuilder />
            </PrivateRoute>
          }
        />
        <Route path={`/:eventId${URL.VB_ASSIGNEE}`} element={<AssigneeVB />} />
        <Route
          path={`/:eventId${URL.VB_PEOPLE}`}
          element={
            <PrivateRoute allowAssignee>
              <People />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.VB_HL_REEL}`}
          element={
            <PrivateRoute allowAssignee>
              <HighlightReel />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.VB_CLIPS}`}
          element={
            <PrivateRoute allowAssignee>
              <Clips />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.VB_ANALYSIS}`}
          element={
            <PrivateRoute>
              <Analysis />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.VB_REVIEW}`}
          element={
            <PrivateRoute>
              <Review />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.VB_SHARE}`}
          element={
            <PrivateRoute>
              <Share />
            </PrivateRoute>
          }
        />

        {/* project pages */}
        {[
          URL.PROJECT_SETTINGS,
          `/:eventId${URL.PROJECT_SETTINGS}`,
          `/:eventId${URL.PROJECT_SETTINGS}/:tab`,
          `/:eventId${URL.PROJECT_SETTINGS}/:tab/:subtab`,
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <PrivateRoute>
                <ProjectSettings />
              </PrivateRoute>
            }
          />
        ))}
        <Route
          path={`/:eventId${URL.PROJECT_ANALYTICS}`}
          element={
            <PrivateRoute>
              <ProjectAnalytics />
            </PrivateRoute>
          }
        />
        <Route
          path={`/:eventId${URL.PROJECT_MISSING_ASSETS}`}
          element={
            <PrivateRoute>
              <ProjectMissingAssets />
            </PrivateRoute>
          }
        />

        {/* attendee review video */}
        <Route path={`${URL.REVIEW_VIDEO}:faceId`} element={<ReviewVideo />} />
        <Route
          path={`${URL.REVIEW_VIDEO_GENERATING}:faceId`}
          element={<ReviewVideoGenerating />}
        />

        {/* check in */}
        <Route path={`${URL.CHECK_IN}:eventId`} element={<EventCheckIn />} />
        <Route
          path={`${URL.CHECK_IN}:eventId/:faceId`}
          element={<EventCheckIn />}
        />
        <Route path={URL.CHECK_IN_REDIRECT} element={<CheckInRedirect />} />

        {/* share */}
        <Route path={`${URL.SHARE}:eventId`} element={<ShareHub />} />
        <Route path="/share/:eventId/:faceId" element={<ShareVideo />} />
        {/* Unable to navigate with constant URL, will investigate later */}
        {/* <Route
          path={`${URL.SHARE}:eventId/:faceId}`}
          element={<ShareVideo />}
        /> */}

        {/* attendee portal – demo */}
        <Route
          path={URL.ATTENDEE_PORTAL}
          element={
            <PrivateRoute>
              <AttendeePortal />
            </PrivateRoute>
          }
        />
        <Route
          path={`${URL.ATTENDEE_PORTAL}/:eventId`}
          element={
            <PrivateRoute>
              <AttendeeEvent />
            </PrivateRoute>
          }
        />

        {/* other  */}
        <Route path={URL.DEMO} element={<Demo />} />

        {[URL.HELP, `${URL.HELP}/:topicSlug`].map((path) => (
          <Route key={path} path={path} element={<HelpMain />} />
        ))}
        <Route
          path={`${URL.HELP}/:topicSlug/:articleSlug`}
          element={<HelpArticle />}
        />

        <Route path={URL.MANAGED_SERVICES} element={<ManagedServices />} />
        <Route
          path={`${URL.MANAGED_SERVICES}:eventId`}
          element={<ManagedServices />}
        />

        <Route path={URL.PAYMENT_REDIRECT} element={<PaymentRedirect />} />

        {/* third-party auth */}
        <Route path={URL.LINKEDIN_CALLBACK} element={<LinkedInCallback />} />

        {/* all non-existent URLs */}
        <Route path="*" element={<Error code={404} />} />
      </Routes>

      <ToastContainer position={toast.POSITION.TOP_CENTER} autoClose={5000} />
    </div>
  );
};

export default App;
